/**
 * Portal interaction tracking service.
 *
 * Buffers user events and flushes them in batches to
 * POST /api/portal/events/batch. Designed to be non-blocking:
 * - Buffers up to MAX_BUFFER events or FLUSH_INTERVAL_MS, whichever comes first
 * - Uses navigator.sendBeacon on page unload for reliability
 * - Silently drops events on network failure (analytics, not critical path)
 */

import api from './api';
import { tokenService } from './api';
import { RECORDED_BY_DOM_KEY, forwardTrackEvent } from './guruRecorderBridge';

// --- Configuration ---
const FLUSH_INTERVAL_MS = 5_000;
const MAX_BUFFER = 20;
const MAX_BATCH_SIZE = 100;

// --- Types ---
export type PortalEventType =
  | 'click'
  | 'navigation'
  | 'search'
  | 'action'
  | 'view'
  | 'filter';

interface QueuedEvent {
  event_type: PortalEventType;
  event_target: string;
  page: string;
  metadata: Record<string, unknown> | null;
  session_id: string | null;
  client_timestamp: string;
}

// --- State ---
let buffer: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let sessionId: string | null = null;

function getSessionId(): string {
  if (!sessionId) {
    sessionId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return sessionId;
}

/**
 * Queue a portal interaction event for batch submission.
 *
 * Call this from anywhere in the portal frontend. Events are buffered
 * and flushed automatically — callers don't need to await anything.
 *
 * Every event is ALSO forwarded to the shared Guru user-action recorder
 * (services/guruRecorderBridge.ts, gurucloudai kanban b91c17bf) when its
 * script is loaded: the target becomes the action name, id-like metadata
 * keys become targets, chosen values (tab/filter/query) become values, and
 * navigation events set the screen. The portal_events rows are unchanged,
 * except that the recorder-only `recorded_by_dom` flag (the control's own
 * data-action row already covers it) is stripped before queuing.
 */
export function trackEvent(
  eventType: PortalEventType,
  eventTarget: string,
  metadata?: Record<string, unknown> | null,
): void {
  forwardTrackEvent(eventType, eventTarget, metadata);
  let stored: Record<string, unknown> | null = metadata ?? null;
  if (stored && RECORDED_BY_DOM_KEY in stored) {
    stored = { ...stored };
    delete stored[RECORDED_BY_DOM_KEY];
  }
  const event: QueuedEvent = {
    event_type: eventType,
    event_target: eventTarget,
    page: window.location.pathname,
    metadata: stored,
    session_id: getSessionId(),
    client_timestamp: new Date().toISOString(),
  };

  buffer.push(event);

  if (buffer.length >= MAX_BUFFER) {
    flush();
  }
}

/**
 * Queue an event AND flush it right away.
 *
 * For rare, high-value interactions rendered by components that are shared
 * across layouts (the agent-file cards live on portal doors AND on admin pages
 * such as /agent-console, /cplex/sankey and the Merlin monitor). Only
 * PortalLayout starts the periodic flush timer, so a plain trackEvent() from an
 * admin page would sit in the buffer until 20 events accumulate — and be lost
 * on navigation. One POST per click is fine at these volumes.
 */
export function trackEventNow(
  eventType: PortalEventType,
  eventTarget: string,
  metadata?: Record<string, unknown> | null,
): void {
  trackEvent(eventType, eventTarget, metadata);
  flush();
}

/**
 * Flush buffered events to the backend.
 * Safe to call at any time — no-ops if buffer is empty.
 */
export function flush(): void {
  if (buffer.length === 0) return;

  // Grab current buffer and reset immediately so new events
  // don't get lost while the request is in flight.
  const batch = buffer.slice(0, MAX_BATCH_SIZE);
  buffer = buffer.slice(MAX_BATCH_SIZE);

  // Fire-and-forget — analytics should never block the UI
  api
    .post('/portal/events/batch', { events: batch })
    .catch(() => {
      // Silently drop on failure. Analytics are best-effort.
    });
}

/**
 * Flush via sendBeacon (used on page unload).
 * sendBeacon is more reliable than fetch during unload.
 */
function beaconFlush(): void {
  if (buffer.length === 0) return;

  const batch = buffer.slice(0, MAX_BATCH_SIZE);
  buffer = [];

  const token = tokenService.getAccessToken();
  const baseUrl = import.meta.env.VITE_API_URL || '/api';
  const url = `${baseUrl}/portal/events/batch`;

  const blob = new Blob(
    [JSON.stringify({ events: batch })],
    { type: 'application/json' },
  );

  // sendBeacon doesn't support custom headers, so we append
  // the token as a query param. The backend should also accept
  // this for beacon requests, or we fall back to regular flush.
  if (token) {
    // Try fetch with keepalive first (supports headers)
    try {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      }).catch(() => {
        // Last resort: sendBeacon without auth (will 401, events lost)
        navigator.sendBeacon(url, blob);
      });
    } catch {
      navigator.sendBeacon(url, blob);
    }
  } else {
    navigator.sendBeacon(url, blob);
  }
}

/**
 * Start the periodic flush timer and register unload handler.
 * Call once when the portal layout mounts.
 */
export function startTracking(): void {
  if (flushTimer) return; // Already started

  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  window.addEventListener('beforeunload', beaconFlush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
}

/**
 * Stop tracking and flush remaining events.
 * Call when the portal layout unmounts.
 */
export function stopTracking(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush();
  window.removeEventListener('beforeunload', beaconFlush);
}

/**
 * Track a page navigation event. Called by usePortalPageViews for the screen
 * the portal layout mounts on and for every later route change. `extra` is
 * merged into the metadata next to `path` (the landing view passes
 * `{ landing: true }`).
 */
export function trackNavigation(path: string, extra?: Record<string, unknown>): void {
  trackEvent('navigation', 'page_view', extra ? { path, ...extra } : { path });
}
